import { useEffect, useState } from "react";
import {
  getFacilitySettings,
  updateFacilitySettings,
} from "../api/facilitySettings";
import AdminNav from "../components/AdminNav";

const modeLabels = {
  manual_review: "Manual Review",
  trusted_tunnel: "Trusted Tunnel",
};

export default function FacilitySettings() {
  const [settings, setSettings] = useState(null);
  const [mode, setMode] = useState("manual_review");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const loadSettings = async () => {
    setLoading(true);
    setMessage("");

    try {
      const res = await getFacilitySettings();
      const data = res.data.data;

      setSettings(data);
      setMode(data.attorney_upload_mode || "manual_review");
    } catch (err) {
      setMessage("Unable to load facility settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const saveSettings = async () => {
    const warning =
      mode === "trusted_tunnel"
        ? "Trusted Tunnel will automatically release attorney uploads to approved inmates after attorney/facility/inmate access is already approved. Continue?"
        : "Manual Review requires facility staff to approve and release attorney uploads before inmates can view them. Continue?";

    const confirmed = await window.confirm(warning);

    if (!confirmed) {
      return;
    }

    setSaving(true);
    setMessage("");

    try {
      const res = await updateFacilitySettings({
        attorney_upload_mode: mode,
      });

      setSettings(res.data.data);
      setMessage("Facility settings saved.");
    } catch (err) {
      setMessage(err.response?.data?.message || "Unable to save facility settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="app-shell">
      <div className="topbar d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <div className="brand-mark">CV</div>

          <div>
            <h5 className="mb-0">CounselVault</h5>
            <div className="small opacity-75">Facility Settings</div>
          </div>
        </div>

        <AdminNav />
      </div>

      <main className="page-wrap">
        <div className="mb-4">
          <h2 className="mb-1">Facility Settings</h2>
          <p className="text-muted mb-0">
            Control how privileged legal documents move through your facility workflow.
          </p>
        </div>

        {message && (
          <div className="alert alert-info py-2">
            {message}
          </div>
        )}

        {loading ? (
          <div className="portal-card">
            <div className="portal-card-body">
              Loading facility settings...
            </div>
          </div>
        ) : (
          <div className="portal-card">
            <div className="portal-card-header">
              <h5 className="mb-0">{settings?.facility_name || "Facility"}</h5>
              <div className="text-muted small">
                Current mode: {modeLabels[settings?.attorney_upload_mode] || "Manual Review"}
              </div>
            </div>

            <div className="portal-card-body">
              <div className="mb-4">
                <label className="form-label fw-bold">
                  Attorney Upload Workflow
                </label>

                <select
                  className="form-select form-select-lg"
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                >
                  <option value="manual_review">
                    Manual Review — facility must approve and release
                  </option>
                  <option value="trusted_tunnel">
                    Trusted Tunnel — automatically release after access is approved
                  </option>
                </select>
              </div>

              <div className="alert alert-light border">
                {mode === "manual_review" ? (
                  <>
                    <strong>Manual Review:</strong>{" "}
                    Attorneys can upload only after approved access exists. Facility staff still controls document approval and release before the inmate can view it.
                  </>
                ) : (
                  <>
                    <strong>Trusted Tunnel:</strong>{" "}
                    Once attorney facility access and attorney-inmate access are approved, attorney uploads are automatically released to the inmate. Access checks, audit logging, private storage, watermarking, and revocation enforcement still apply.
                  </>
                )}
              </div>

              <button
                className="btn btn-dark"
                onClick={saveSettings}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Facility Settings"}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
